<template>
    <div>
        <Card v-if="!loading" class="vat-settings">
            <VatPopupManager
                v-if="shouldDisplayVatManagerPopup"
                @close="shouldDisplayVatManagerPopup = false"
            />
            <CatRatePopupManager
                v-if="shouldDisplayCatRatePopupManager"
                :rateCategory="selectedRateCategory"
                @close="shouldDisplayCatRatePopupManager = false"
            />
            <!--------------------TVA PAR DEFAUT-------------------->
            <div class="vat-settings__default-vat bloc">
                <div class="typographies-heading-h3">{{ $l10n.__('TVA par défaut') }}</div>
                <div class="mb-3 typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Le taux de TVA par défaut sera utilisé lors de la création des produits et des services.',
                        )
                    }}
                </div>
                <div class="flex-row">
                    <el-select
                        ref="selectVatByDefault"
                        v-model="taxesSettings.vat_id"
                        placeholder="Select"
                    >
                        <el-option
                            v-for="item in listNoEcoTaxes"
                            :key="item.id"
                            :label="item | taxeLabel"
                            :value="item.id"
                            data-test="vatoption"
                        >
                        </el-option>
                    </el-select>
                    <CompassButton
                        type="tertiary"
                        :label="$t.__('common.actions.edit')"
                        class="ml-3"
                        @click="shouldDisplayVatManagerPopup = true"
                    />
                </div>
            </div>
            <!--------------------MODE DE TVA-------------------->
            <div class="modeTva bloc">
                <div class="mb-3 typographies-heading-h3">
                    {{ $l10n.__('Mode de TVA') }}
                </div>
                <div class="mb-3 typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'En fonction de la typologie de votre entreprise, choisissez votre mode de TVA. Il sera applicable sur tous vos nouveaux documents.',
                        )
                    }}
                </div>
                <el-select
                    v-model="taxesSettings.vat_mode"
                    class="tva-mode-select"
                    placeholder="Select"
                >
                    <el-option
                        v-for="item in tvaModes"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                    >
                    </el-option>
                </el-select>
            </div>
            <!--------------------MENTION MODE DE TVA-------------------->
            <div class="modeTva bloc">
                <div class="mb-3 typographies-heading-h3">
                    {{ $l10n.__('Mention du mode de TVA sur les documents de vente') }}
                </div>
                <div class="mb-3 typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Si vous activez cette option, la mention de TVA ci-dessous sera affichée sur les PDF de vos documents de vente.',
                        )
                    }}
                </div>
                <SwitchWithLabel
                    ref="switchTaxMention"
                    :textLabel="
                        $t.__('settings.taxes.VatSettings.activate_VAT_mode_on_sales_documents')
                    "
                    :value="taxesSettings.display_tax_mention"
                    @change="updateDisplayMention($event)"
                />
                <div class="pb-1 pt-3 typographies-heading-h4">{{ $l10n.__('Mention') }}</div>
                <el-input
                    ref="inputTaxMention"
                    v-model="taxesSettings.vat_mention"
                    :disabled="!taxesSettings.display_tax_mention"
                    maxlength="255"
                    class="vat-settings__input-vat-mention"
                />
            </div>

            <el-divider class="bloc" />

            <!---------------------------------------CATEGORIES TARIFAIRES--------------------------------------->
            <div class="bloc catTarif">
                <div class="mb-3 typographies-heading-h2">
                    {{ $l10n.__('Catégories tarifaires') }}
                </div>
                <CompassButton
                    data-upsell="rate_categories"
                    data-quota-guard="rate_categories"
                    :disabled="false"
                    :label="$t.__('settings.taxes.VatSettings.add_price_category')"
                    class="mb-4"
                    @click="openPopupRateCategory()"
                />
                <el-table :data="rateCategories" max-height="648" class="vat-settings-table">
                    <el-table-column className="pl-3 vat-settings__not-hover" label="" width="48">
                        <template #default="scope">
                            <el-radio
                                v-model="taxesSettings.rate_category_id"
                                :label="scope.row.id"
                            />
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t.__('settings.taxes.VatSettings.category')"
                        className="px-3"
                        prop="label"
                        sortable
                        width="219px"
                    >
                        <template #default="scope">
                            <div class="typographies-heading-h4">
                                {{ scope.row.label }}
                            </div>
                            <div
                                v-if="scope.row.price.type === 'value'"
                                class="vat-settings__text_secondary typographies-paragraph-small-regular"
                            >
                                {{ $l10n.__('Saisie libre du prix') }}
                            </div>
                            <div
                                v-else
                                class="vat-settings__text_secondary typographies-paragraph-small-regular"
                            >
                                {{ scope.row.price.percent }}
                                {{ $l10n.__(' % sur le prix de référence du produit / service') }}
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t.__('settings.taxes.VatSettings.documents')"
                        className="px-3"
                        prop="includes_taxes"
                        sortable
                        width="133px"
                    >
                        <template #default="scope">
                            <template v-if="scope.row.includes_taxes">
                                {{ $l10n.__('TTC') }}
                            </template>
                            <template v-else>
                                {{ $l10n.__('HT') }}
                            </template>
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t.__('settings.taxes.VatSettings.VAT_applied')"
                        className="px-3"
                        prop="tva"
                        sortable
                        width="153px"
                    >
                        <template #default="scope">
                            {{ labelForTva(scope.row) }}
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t.__('settings.taxes.VatSettings.documents_currency')"
                        className="px-3"
                        prop="currency"
                        sortable
                        width="204px"
                    >
                        <template #default="scope">
                            <div class="typographies-heading-h4">
                                {{ scope.row._embed.currency.symbol }}
                            </div>
                            <div
                                v-if="shouldDisplayIsAccountCurrency(scope.row)"
                                class="vat-settings__text_secondary typographies-paragraph-small-regular"
                            >
                                {{ $l10n.__('Devise du compte') }}
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t.__('common.labels.accounting_code')"
                        className="px-3"
                        prop="accounting_code_id"
                        sortable
                        width="165px"
                    >
                        <template #default="scope">
                            {{
                                scope.row.accounting.accounting_code_id === null
                                    ? $l10n.__('Code comptable produit')
                                    : getAccountingCodeById(scope.row.accounting.accounting_code_id)
                            }}
                        </template>
                    </el-table-column>
                    <el-table-column className="pl-2 vat-settings__not-hover" width="52px">
                        <template #default="scope">
                            <el-dropdown placement="bottom-end">
                                <CompassIconButton icon="action" size="small" />
                                <template #dropdown>
                                    <el-dropdown-menu class="discount-dropdown">
                                        <el-dropdown-item
                                            @click.native="openPopupRateCategory(scope.row)"
                                        >
                                            <span>{{ $l10n.__('Editer') }}</span>
                                        </el-dropdown-item>
                                        <el-dropdown-item
                                            v-if="scope.row.id != taxesSettings.rate_category_id"
                                            ref="dropdownItemDeleteRateCategory"
                                            @click.native="onDeleteRateCategory(scope.row.id)"
                                        >
                                            {{ $l10n.__('Supprimer') }}
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
            <!--------------------REMISES PAR DEFAUT-------------------->
            <div class="bloc vat-settings__default-discount">
                <div class="mb-3 typographies-heading-h3">{{ $l10n.__('Remises par défaut') }}</div>
                <div class="flex-column mb-3 vat-settings__default-discount-select">
                    <InputWithSelect
                        ref="inputWithSelectDiscount"
                        v-model="vatDiscountValue"
                        :options="discountOptions"
                        @blur="onBlurVatDiscountValue"
                    />
                    <div
                        v-if="displayErrorOnInputDiscount"
                        class="pt-1 typographies-paragraph-small-regular vat-settings__text_error"
                    >
                        {{
                            $l10n.__(
                                'Merci de rentrer une valeur valide (pas plus de deux décimals)',
                            )
                        }}
                    </div>
                </div>
                <SwitchWithLabel
                    ref="switchByRows"
                    :textLabel="
                        $t.__('settings.taxes.VatSettings.each_product__can_have_specific_discount')
                    "
                    :value="taxesSettings.discount.by_rows"
                    @change="updateDiscountByRow($event)"
                />
            </div>
            <!--------------------ECOTAXE-------------------->
            <div class="bloc ecotax">
                <div class="mb-3 typographies-heading-h3">{{ $l10n.__('Écotaxe') }}</div>
                <SwitchWithLabel
                    :textLabel="$t.__('settings.taxes.VatSettings.enable_eco_tax')"
                    :value="taxesSettings.use_ecotax"
                    @change="updateUseEcotax($event)"
                />
            </div>
            <!--------------------REMISE TTC-------------------->
            <div class="bloc discountTTC">
                <div class="bloc mb-3 typographies-heading-h3">{{ $l10n.__('Remise TTC') }}</div>
                <SwitchWithLabel
                    :textLabel="$t.__('settings.taxes.VatSettings.enable_discount_including_tax')"
                    :value="taxesSettings.use_bonuses"
                    @change="updateUseBonuses($event)"
                />
            </div>
        </Card>
        <preloader :display="loading" />
    </div>
</template>
<script>
import Card from 'vcomponents/common/card/Card.vue';
import InputWithSelect from 'vcomponents/inputs/InputWithSelect.vue';
import SwitchWithLabel from 'vcomponents/inputs/SwitchWithLabel.vue';
import VatPopupManager from 'vcomponents/settings/taxes/popup/VatPopupManager.vue';
import CatRatePopupManager from 'vcomponents/settings/taxes/popup/CatRatePopupManager.vue';
import vatStore from 'stores/taxes/vatStore';
import rateCategoriesStore from 'stores/rateCategories/rateCategoriesStore';
import languagesStore from 'stores/languages/languagesStore';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import Feature from 'Services/Feature';

import { createNamespacedHelpers } from 'vuex';

const vat = createNamespacedHelpers('vat');
const rateCategories = createNamespacedHelpers('rateCategories');
const languages = createNamespacedHelpers('languages');

export default {
    filters: {
        taxeLabel(value) {
            return value.label
                ? `${value.label} - ${parseFloat(value.rate)} %`
                : `${parseFloat(value.rate)} %`;
        },
    },
    components: {
        VatPopupManager,
        InputWithSelect,
        SwitchWithLabel,
        Preloader,
        CatRatePopupManager,
        Card,
    },
    data() {
        return {
            store: vatStore,
            storeLanguage: languagesStore,
            rateCategoriesStore: rateCategoriesStore,
            discount: {},
            discountOptions: [
                {
                    label: '%',
                    value: 'percent',
                },
                {
                    label: this.$store.state['corp']['numformat']['currencysymbol'],
                    value: 'amount',
                },
            ],
            shouldDisplayVatManagerPopup: false,
            shouldDisplayCatRatePopupManager: false,
            loading: true,
            selectedRateCategory: null,
            displayErrorOnInputDiscount: false,
            validatorDefaultRate: /^(\d+[.,]?\d{0,2})$/,
            tvaModes: [
                { value: 'debit', label: this.$l10n.__('TVA sur les débits') },
                { value: 'collection', label: this.$l10n.__('TVA sur les encaissements') },
            ],
        };
    },
    computed: {
        ...vat.mapGetters(['taxes', 'taxesSettings']),
        ...rateCategories.mapGetters(['rateCategories', 'rateCategoriesMetadata']),
        ...languages.mapGetters(['languages']),
        listNoEcoTaxes() {
            return this.$store.getters['vat/listNoEcoTaxes'].filter((tax) => tax.is_active);
        },
        vatDiscountValue: {
            get() {
                return this.discount;
            },
            set(value) {
                this.discount = value;
                this.updateTaxesDiscountValue(value);
            },
        },
    },
    created() {
        this.$store.registerModule('vat', this.store);
        this.$store.registerModule('rateCategories', this.rateCategoriesStore);
        this.$store.registerModule('languages', this.storeLanguage);
        Promise.all([
            this.fetchTaxes(),
            this.fetchTaxesSettings(),
            this.fetchLanguages(),
            this.fetchRateCategories(),
            this.fetchRateCategoriesMetadata(),
        ])
            .then(() => {
                this.discount = {
                    input: this.taxesSettings.discount.value,
                    select: this.taxesSettings.discount.type,
                };
                this.initialiseWatchers();
            })
            .catch((err) => {
                Push.error(this.$l10n.__('Une erreur est survenue' + err));
            })
            .finally(() => {
                this.loading = false;
                this.$nextTick(() => {
                    // we have to call the method again due to the v-if on the template which manages the loading state
                    window.Quota.checkQuotas(['rate_categories']);
                });
            });
    },

    beforeDestroy() {
        if (this.validatorDefaultRate.test(this.discount.input)) {
            this.taxesSettings.discount.value = +this.discount.input;
            this.updateTaxesSettings();
        }
    },
    methods: {
        ...vat.mapActions([
            'fetchTaxes',
            'fetchTaxesSettings',
            'updateTaxesSettings',
            'updateTaxesDiscountValue',
        ]),
        ...rateCategories.mapActions([
            'fetchRateCategories',
            'updateRateCategory',
            'fetchRateCategoriesMetadata',
            'deleteRateCategory',
        ]),
        ...languages.mapActions(['fetchLanguages']),
        openPopupRateCategory(category) {
            if (category && category._embed.currency.is_default) {
                category.currency = null;
            }
            if (category && !category.price.value) {
                category.price.value = 0;
            }
            this.selectedRateCategory = category;
            this.shouldDisplayCatRatePopupManager = true;
        },
        onDeleteRateCategory(id) {
            this.deleteRateCategory(id)
                .then(() => {
                    Push.success(this.$l10n.__('Votre catégorie tarifaire a bien été supprimée'));
                })
                .catch(() => {
                    Push.error(this.$l10n.__('Une erreur est survenue lors de la suppression'));
                });
        },
        initialiseWatchers() {
            this.$watch('$store.state.vat.taxesSettings', {
                handler: _.debounce(function (newValue, oldValue) {
                    if (oldValue) {
                        this.updateTaxesSettings().catch(() => {
                            Push.error(
                                this.$l10n.__("Une erreur est survenue lors de l'enregistrement."),
                            );
                        });
                    }
                }, 500),
                deep: true,
            });
            this.$watch('discount.select', {
                handler: function (newVal) {
                    this.taxesSettings.discount.type = newVal;
                },
            });
        },
        onBlurVatDiscountValue() {
            if (this.validatorDefaultRate.test(this.discount.input)) {
                this.displayErrorOnInputDiscount = false;
                this.taxesSettings.discount.value = String(this.discount.input).replace(',', '.');
            } else {
                this.displayErrorOnInputDiscount = true;
            }
        },
        shouldDisplayIsAccountCurrency(rateCategory) {
            return rateCategory._embed?.currency.is_default ?? false;
        },
        labelForTva(rateCategory) {
            const tax = this.taxes.find((item) => item.id === rateCategory.tax_id);
            return tax ? `${parseFloat(tax.rate)} %` : this.$l10n.__('Taxe du produit');
        },
        updateDisplayMention(value) {
            this.taxesSettings.display_tax_mention = value;
        },
        updateUseEcotax(value) {
            this.taxesSettings.use_ecotax = value;
        },
        updateUseBonuses(value) {
            this.taxesSettings.use_bonuses = value;
        },
        updateDiscountByRow(value) {
            this.taxesSettings.discount.by_rows = value;
        },
        getAccountingCodeById(id) {
            const code = this.rateCategoriesMetadata.accounting.accounting_codes.find(
                (item) => item.value === id,
            );
            return code?.code ?? '';
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.vat-settings {
    &__title {
        margin: 0 0 var(--spacing-4);
    }

    .bloc {
        margin-bottom: 40px;

        &:last-child {
            margin-bottom: 0;
        }

        &.el-divider--horizontal {
            margin-top: 40px;
        }

        .input-with-select.el-input.el-input-group {
            .el-input-group__append {
                background-color: $white;
                color: $gray-100;
            }
        }

        .cell {
            overflow: hidden;
            padding-left: 0px;
            padding-right: 0px;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .el-radio__label {
            display: none;
        }
        &.vat-settings__default-vat,
        &.tva-mode-select {
            .el-select .el-input {
                width: 287px;
            }
        }
        &.vat-settings__default-discount {
            width: fit-content;
            .vat-settings__default-discount-select {
                overflow: hidden hidden;
            }
        }
        &.vat-settings__default-discount .input-with-select {
            width: 287px;
            & > .el-input__inner {
                width: 100%;
            }
            .el-input-group__append {
                width: 94px;
                padding: 0;

                .el-input__inner {
                    transition: none;
                }
            }

            &--open {
                .el-input-group__append {
                    border: 1px solid $primary-50;
                    padding-left: 1px;
                    width: 95px !important;

                    .el-input__inner {
                        border: none;
                    }
                }

                .el-input__inner {
                    border-right-width: 0;
                }
            }
        }
    }
    .vat-settings__text_secondary {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--colors-common-text-subtle-default);
    }
    .vat-settings__text_error {
        color: var(--colors-common-text-alert-default);
    }
    .vat-settings__input-vat-mention {
        width: 287px;
    }
    .vat-settings-table tr {
        height: 56px;
    }
    .vat-settings-table tr th:not(.vat-settings__not-hover):hover {
        background-color: $gray-0;
        height: 56px;
    }
    .vat-settings-table .caret-wrapper {
        display: none;
        margin-left: 8px;
        right: unset !important;
    }
    .vat-settings-table tr th:hover .caret-wrapper {
        display: inline-flex;
    }

    .tva-mode-select {
        width: 287px;
    }

    .upsell-action {
        background-color: var(--colors-common-background-disabled);
        .compass-button__label {
            color: var(--colors-common-text-standard-disabled);
        }

        &:hover {
            background-color: var(--colors-common-background-disabled);
        }

        .compass-feature-badge {
            position: absolute;
            right: 0;
            top: 0;
            transform: translate3d(4px, -4px, 0);
        }
    }
}

.el-popper.discount-dropdown {
    margin-top: 4px !important;

    .popper__arrow {
        display: none !important;
    }
}
</style>
